import {useEffect, useRef} from 'react'

import {waitForConsentPreferences} from '@github-ui/cookie-consent'
import {doNotTrack} from '@github-ui/do-not-track'

import type {VendorPixel} from '../types/analytics/vendor-pixel'
import {fireVendorPixel} from './fire-vendor-pixel'

/**
 * Fires page-view vendor pixels once on mount, respecting user privacy preferences.
 *
 * Pixels are only fired when:
 * - `vendorPixels` is non-empty and at least one pixel has a `pageViewUrl`
 * - Do Not Track / Global Privacy Control is not enabled
 * - The user has consented to Analytics cookies
 *
 * Each vendor's page-view pixel fires at most once per component mount.
 */
export function usePixelsOnPageview(vendorPixels: VendorPixel[]): void {
  // This keeps track of the pixel vendor keys that have fired to make sure it fires only once
  const firedKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const pixelsWithPageView = vendorPixels.filter(p => p.pageViewUrl)
    if (pixelsWithPageView.length === 0) return

    if (doNotTrack()) return

    // Guard against attaching listeners after unmount (waitForConsentPreferences is async)
    let unmounted = false
    const firedKeys = firedKeysRef.current

    async function firePageViewPixels() {
      const consentPreferences = await waitForConsentPreferences()
      if (unmounted) return
      if (!consentPreferences || !consentPreferences['Analytics']) return

      for (const pixel of pixelsWithPageView) {
        if (firedKeys.has(pixel.key)) continue

        // Create and fire pixel
        fireVendorPixel(pixel.pageViewUrl!)
        firedKeys.add(pixel.key)
      }
    }

    firePageViewPixels()

    return () => {
      unmounted = true
      // Reset so pixels can fire again on next mount / page visit
      firedKeys.clear()
    }
  }, [vendorPixels])
}
